import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { AnnotationStatus } from '@rossum/api-client/shared';
import { getIDFromUrl } from '@rossum/api-client/utils';
import { IconTrash } from '@rossum/ui/icons/tabler';
import {
  Button,
  ButtonBaseActions,
  List,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { RoleProps, RoleProvider } from '../../../../components/Restrictors';
import { isEmbedded } from '../../../../constants/config';
import { annotationSelector } from '../../../../redux/modules/annotation/selectors';
import { deleteAnnotations } from '../../../../redux/modules/annotations/actions';
import { isDeleteRecommendationInPathSelector } from '../../../../redux/modules/datapoints/selector';
import { isUserAdminLike } from '../../../../redux/modules/groups/helpers';
import { MatchedTriggerRulesPerLevel } from '../../../../types/datapoints';
import { GroupRole } from '../../../../types/group';
import { SidebarContainer } from '../../../document-sidebar';
import { useBackToDocument } from '../back-to-document/useBackToDocument';
import { MatchedCriteria } from './MatchedCriteria';
import { useMatchedRules } from './useMatchedRules';

type DeleteRecommendationProps = {
  matchedTriggerRules: MatchedTriggerRulesPerLevel;
};

const canManageAnnotation = (role: GroupRole, status: AnnotationStatus) => {
  const isAdmin = isUserAdminLike(role);
  const finalStates: Array<AnnotationStatus> = [
    'exported',
    'failed_export',
    'deleted',
    'confirmed',
  ];
  return isAdmin && !finalStates.includes(status) && !isEmbedded();
};

export const DeleteRecommendation = ({
  matchedTriggerRules,
}: DeleteRecommendationProps) => {
  const intl = useIntl();

  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const actionRef = useRef<ButtonBaseActions>(null);

  const dispatch = useDispatch();
  const shouldFocusDeleteButton = useSelector(
    isDeleteRecommendationInPathSelector
  );

  const annotation = useSelector(annotationSelector);

  const queueId = getIDFromUrl(annotation?.queue || '');

  const matchedRules = useMatchedRules(matchedTriggerRules);

  // Focus delete button when all other focuses in dp navigation are disabled
  useEffect(() => {
    if (
      shouldFocusDeleteButton &&
      deleteButtonRef.current &&
      actionRef.current
    ) {
      deleteButtonRef.current.focus();
      actionRef.current.focusVisible();
    }
  }, [shouldFocusDeleteButton]);

  const { triggerBackToDocumentToast } = useBackToDocument();

  return (
    <SidebarContainer
      title={intl.formatMessage({
        id: 'components.sidebarV2.deleteRecommendations.title',
      })}
    >
      <Stack direction="row" spacing={1}>
        <SvgIcon fontSize="medium" color="warning">
          <IconTrash />
        </SvgIcon>

        <Stack>
          <Typography variant="body2" color="text.secondary">
            {intl.formatMessage({
              id: 'components.sidebarV2.deleteRecommendations.text',
            })}
          </Typography>
          <List
            disablePadding
            dense
            sx={{
              listStyleType: 'disc',
              color: 'text.secondary',
              pl: 3,
            }}
          >
            {matchedRules.map(rule => (
              <MatchedCriteria
                key={`${'id' in rule ? rule.id : 'matched-criteria'}-${rule.type}`}
                conditionType={rule.type}
                value={rule.ruleValue}
                enumLabel={'label' in rule ? rule.label : undefined}
              />
            ))}
          </List>
        </Stack>
      </Stack>
      {annotation && (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <RoleProvider>
            {({ role }: RoleProps) => {
              const displaySettingsLink = canManageAnnotation(
                role,
                annotation.status
              );
              return displaySettingsLink ? (
                <Button
                  component={RouterLink}
                  to={{
                    pathname: `/queues/${queueId}/settings/basic`,
                    state: {
                      backLink: `/document/${annotation.id}/`,
                      openTriageRules: true,
                    },
                  }}
                  onClick={triggerBackToDocumentToast}
                  data-cy="queue-settings-triage"
                  color="secondary"
                  variant="outlined"
                  sx={{
                    '&:hover': {
                      color: theme => theme.palette.secondary.main,
                    },
                  }}
                >
                  {intl.formatMessage({
                    id: 'components.sidebarV2.deleteRecommendations.button.settings',
                  })}
                </Button>
              ) : null;
            }}
          </RoleProvider>
          <Button
            ref={deleteButtonRef}
            action={actionRef}
            variant="contained"
            color="primary"
            data-cy="triage-rules-button-delete"
            startIcon={
              <SvgIcon fontSize="large">
                <IconTrash />
              </SvgIcon>
            }
            onClick={() => dispatch(deleteAnnotations([annotation.url]))}
          >
            {intl.formatMessage({
              id: 'components.sidebarV2.deleteRecommendations.button.delete',
            })}
          </Button>
        </Stack>
      )}
    </SidebarContainer>
  );
};
